/**
 * WizardStepFilters — krok 4/6 kreatora widgetu: opcjonalne warunki zawężające dane.
 *
 * Operatory ograniczone do podzbioru porównawczego `FilterOperator` (eq/neq/gt/gte/lt/lte) —
 * spec T1.7 świadomie wyklucza contains/starts_with/in/is_null z kreatora MVP. Wiersz bez pola
 * albo bez wartości jest pomijany przy zapisie (`buildFinalConfig` w `WidgetCreatorWizard.tsx`).
 */

import { useTranslation } from 'react-i18next';
import type { FilterOperator } from '@saas/shared';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import type {
  WidgetDefinitionConfig,
  WidgetFilterSpec,
  WidgetSourceMeta,
} from '@/lib/widgets/types';

const COMPARISON_OPERATORS: FilterOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];

export interface WizardStepFiltersProps {
  filters: WidgetFilterSpec[] | undefined;
  source: WidgetSourceMeta | undefined;
  patchConfig: (p: Partial<WidgetDefinitionConfig>) => void;
}

export function WizardStepFilters({ filters, source, patchConfig }: WizardStepFiltersProps) {
  const { t } = useTranslation();
  const rows = filters ?? [];
  const fieldOptions = (source?.fields ?? []).map((f) => ({ value: f.slug, label: f.label }));

  function setRows(next: WidgetFilterSpec[]) {
    patchConfig({ filters: next.length > 0 ? next : undefined });
  }

  function updateRow(index: number, patch: Partial<WidgetFilterSpec>) {
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows([...rows, { field: '', operator: 'eq', value: '' }]);
  }

  function removeRow(index: number) {
    setRows(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3" data-testid="widget-wizard-filters">
      <p className="text-sm text-text-tertiary">{t('tenant.widgets.wizard.filters.intro')}</p>

      {rows.length === 0 ? (
        <p className="text-sm text-text-muted">{t('tenant.widgets.wizard.filters.empty')}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div
              key={i}
              className="flex items-start gap-2"
              data-testid={`widget-wizard-filter-row-${i}`}
            >
              <Dropdown
                className="min-w-0 flex-1"
                value={row.field}
                onChange={(v) => updateRow(i, { field: v })}
                placeholder={t('tenant.widgets.wizard.filters.fieldLabel')}
                options={fieldOptions}
                aria-label={t('tenant.widgets.wizard.filters.fieldLabel')}
                data-testid={`widget-wizard-filter-field-${i}`}
              />
              <Dropdown
                className="w-44 shrink-0"
                value={row.operator}
                onChange={(v) => updateRow(i, { operator: v as FilterOperator })}
                options={COMPARISON_OPERATORS.map((op) => ({
                  value: op,
                  label: t(`tenant.widgets.wizard.operator.${op}`),
                }))}
                aria-label={t('tenant.widgets.wizard.filters.operatorLabel')}
                data-testid={`widget-wizard-filter-operator-${i}`}
              />
              <Input
                containerClassName="min-w-0 flex-1"
                value={typeof row.value === 'string' ? row.value : (row.value?.toString() ?? '')}
                onChange={(e) => updateRow(i, { value: e.target.value })}
                placeholder={t('tenant.widgets.wizard.filters.valuePlaceholder')}
                aria-label={t('tenant.widgets.wizard.filters.valueLabel')}
                data-testid={`widget-wizard-filter-value-${i}`}
              />
              <IconButton
                variant="danger"
                aria-label={t('tenant.widgets.wizard.filters.remove')}
                onClick={() => removeRow(i)}
                data-testid={`widget-wizard-filter-remove-${i}`}
              >
                <Trash2 aria-hidden="true" />
              </IconButton>
            </div>
          ))}
          <p className="text-xs text-text-muted">
            {t('tenant.widgets.wizard.filters.incompleteHint')}
          </p>
        </div>
      )}

      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Plus className="h-4 w-4" />}
        onClick={addRow}
        data-testid="widget-wizard-filter-add"
      >
        {t('tenant.widgets.wizard.filters.add')}
      </Button>
    </div>
  );
}
